<template>
  <div class="editor-outer-container">
    <label v-if="labelText">
      {{ labelText }}
      <a :href="helpLink" v-if="helpLink"><i class="circle help icon"></i></a>
    </label>

    <div class="editor-inner-container">
      <div id="editor" ref="templateEditor" :style="{ height: height }"></div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
.editor-outer-container {
  margin-top: 1rem;
}

.editor-inner-container {
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  margin-bottom: 10px;

  &:focus-within {
    border: 1px solid #85b7d9;
  }
}
#editor {
  margin: 10px;
}

label {
  display: inline-block;
  font-weight: bold;
  margin-bottom: 3px;
  font-size: 0.9em;
}
</style>

<script>
// @flow
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api' // needs to be imported this way in order to include only subset of languages
import _ from 'lodash'

// from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
function escapeRegExp(string) {
  return string?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export default {
  name: 'template-editor',

  props: {
    value: String,
    height: String,
    delimBegin: { type: String, default: '{{' },
    delimEnd: { type: String, default: '}}' },
    variables: Array,
    labelText: String,
    helpLink: String
  },

  data() {
    return {
      editor: null
    }
  },

  computed: {
    escap() {
      return [
        ..._.map(this.variables, variable => [escapeRegExp(variable.name), 'VARIABLE']),
        [escapeRegExp(this.delimBegin), 'DELIM'],
        [escapeRegExp(this.delimEnd), 'DELIM']
      ]
    }
  },

  methods: {
    init() {
      if (this.editor) return

      // Theme
      monaco.editor.defineTheme('TemplateEditorTheme', {
        base: 'vs',
        inherit: true,
        rules: [
          // DELIMS are gray
          { token: 'DELIM', foreground: 'bbbbbb' },
          { token: 'VARIABLE', fontStyle: 'bold' }
        ],

        colors: {
          'editor.lineHighlightBackground': '#FFFFFF',
          'editorLineNumber.foreground': '#008800',
          'editorWarning.foreground': '#FFCC00',
          'editorInfo.foreground': '#FFCC00'
        }
      })

      // Register a new language
      monaco.languages.register({ id: 'template' })

      // Register a tokens provider for the language
      monaco.languages.setMonarchTokensProvider('template', {
        tokenizer: {
          root: [
            ..._.map(this.variables, variable => [escapeRegExp(variable.name), 'VARIABLE']),
            [escapeRegExp(this.delimBegin), 'DELIM'],
            [escapeRegExp(this.delimEnd), 'DELIM']
          ]
        }
      })

      // Register a completion item provider for the new language
      monaco.languages.registerCompletionItemProvider('template', {
        provideCompletionItems: () => {
          var suggestions = [
            ..._.map(this.variables, v => ({
              label: v.description,
              kind: monaco.languages.CompletionItemKind.Text,
              insertText: this.delimBegin + v.name + this.delimEnd
            }))
          ]
          return { suggestions }
        }
      })

      this.editor = monaco.editor.create(this.$refs.templateEditor, {
        value: this.value,
        language: 'template',
        theme: 'TemplateEditorTheme',
        tabSize: 2,
        codeLens: false,
        glyphMargin: false,
        lineNumbers: 'off',
        wordWrap: 'on',
        folding: false,
        // Undocumented see https://github.com/Microsoft/vscode/issues/30795#issuecomment-410998882
        lineDecorationsWidth: 0,
        lineNumbersMinChars: 0,
        scrollbar: { vertical: 'hidden', verticalScrollbarSize: 0 },
        minimap: { enabled: false }
      })

      this.editor.onDidChangeModelContent(() => {
        this.$emit('contentchanged', this.editor.getValue())
      })

      // Auto-format
      this.editor.updateOptions({
        autoIndent: true,
        formatOnPaste: true,
        formatOnType: true
      })

      // Compiler options
      monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.ES5,
        noLib: true,
        allowNonTsExtensions: true
      })

      monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
        noLib: true,
        allowNonTsExtensions: true
      })
    }
  },

  mounted() {
    this.init()
  },

  watch: {
    value(v) {
      if (this.editor?.getValue() != v) this.editor?.setValue(v)
    }
  }
}
</script>
